import { useEffect, useState } from 'react'
import { useEditor } from '../store/useEditor'
import type { NightRenderer } from '../render/night/NightRenderer'
import { PRESET_LABEL, PRESET_ORDER, type NightParams } from '../render/night/nightPresets'

/**
 * Developer tuning panel for the night grade. Not for contractors.
 *
 * Every control here writes a shader uniform directly and nothing else - no
 * store update, no re-render of the stage, no bitmap. That is the whole reason
 * the grade moved to the GPU: a tuning drag has to cost a float write and a
 * frame, so that finding the right number is a matter of dragging until it
 * looks right rather than waiting three seconds per guess.
 *
 * The scene statistics are shown because they are what the grade adapts to. If
 * a photo behaves oddly the answer is usually visible in P95 and the hot
 * fraction before it is visible in the picture.
 *
 * GRAY is the most valuable control on here. If the luminance alone does not
 * read as evening then the tones still say midday, and no amount of color
 * work will rescue it - so color is only worth judging once gray passes.
 *
 * Styled inline rather than in a stylesheet: src/styles belongs to the design
 * session (CLAUDE.md), and a temporary developer panel has no business
 * claiming class names there.
 */

type Field = { key: keyof NightParams; label: string; min: number; max: number; step: number }

const FIELDS: Field[] = [
  { key: 'shadowKeep', label: 'Shadow keep', min: 0.05, max: 1, step: 0.01 },
  { key: 'highlightKeep', label: 'Highlight keep', min: 0.005, max: 0.6, step: 0.005 },
  { key: 'knee', label: 'Knee', min: 0.6, max: 3, step: 0.05 },
  { key: 'exposureComp', label: 'Exposure comp (stops)', min: -2, max: 2, step: 0.05 },
  { key: 'chromaStrength', label: 'Chroma strength', min: 0, max: 1, step: 0.01 },
  { key: 'chromaResponse', label: 'Chroma darkening response', min: 0, max: 1, step: 0.01 },
  { key: 'coolShadows', label: 'Shadow coolness', min: 0, max: 0.8, step: 0.01 },
  { key: 'blackFloor', label: 'Black floor', min: 0, max: 0.02, step: 0.0002 },
  { key: 'texturePreservation', label: 'Texture preservation', min: 0, max: 1, step: 0.01 },
  { key: 'contrast', label: 'Contrast', min: -0.2, max: 0.6, step: 0.01 },
  { key: 'vignette', label: 'Vignette', min: 0, max: 0.4, step: 0.01 },
  { key: 'skyExtra', label: 'Sky extra (stops)', min: -4, max: 0, step: 0.05 },
  { key: 'skyFacing', label: 'Sky-facing surfaces', min: 0, max: 1, step: 0.01 },
  { key: 'chromaDarkening', label: 'Colored surfaces lose light', min: 0, max: 0.8, step: 0.01 },
]

export default function NightDebug({ night }: { night: () => NightRenderer | null }) {
  const scene = useEditor((s) => s.doc.scene)
  const setScene = useEditor((s) => s.setScene)
  const [, force] = useState(0)
  const [open, setOpen] = useState(false)

  // The renderer only exists once the stage has a photo, so this waits for it
  // rather than rendering an empty shell.
  useEffect(() => {
    if (night()) return
    const t = setInterval(() => night() && force((n) => n + 1), 300)
    return () => clearInterval(t)
  }, [night])

  const ng = night()
  if (!ng) return null
  const p = ng.getResolved()
  const stats = ng.getStats()

  const box: React.CSSProperties = {
    position: 'fixed',
    right: 12,
    bottom: 12,
    zIndex: 60,
    background: 'rgba(12,12,14,0.94)',
    border: '1px solid #333',
    borderRadius: 10,
    color: '#e6e6e6',
    font: '11px/1.4 ui-monospace, Menlo, monospace',
    padding: 10,
    width: open ? 320 : 'auto',
    maxHeight: '78vh',
    overflowY: 'auto',
  }

  if (!open) {
    return (
      <div style={box}>
        <button
          onClick={() => setOpen(true)}
          style={{ background: 'none', border: 0, color: '#ffd24a', cursor: 'pointer', font: 'inherit' }}
        >
          night tuning
        </button>
      </div>
    )
  }

  const row = (f: Field) => (
    <label key={f.key} style={{ display: 'block', marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{f.label}</span>
        <span style={{ color: '#ffd24a' }}>{Number(p[f.key]).toFixed(4)}</span>
      </div>
      <input
        type="range"
        min={f.min}
        max={f.max}
        step={f.step}
        value={Number(p[f.key])}
        style={{ width: '100%' }}
        onChange={(e) => {
          ng.setOverrides({ [f.key]: parseFloat(e.target.value) } as Partial<NightParams>)
          force((n) => n + 1)
        }}
      />
    </label>
  )

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ color: '#ffd24a' }}>night tuning</strong>
        <button
          onClick={() => setOpen(false)}
          style={{ background: 'none', border: 0, color: '#999', cursor: 'pointer', font: 'inherit' }}
        >
          hide
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {PRESET_ORDER.map((id) => (
          <button
            key={id}
            onClick={() => {
              ng.clearOverrides()
              setScene({ nightPreset: id })
              force((n) => n + 1)
            }}
            style={{
              flex: 1,
              padding: '4px 2px',
              background: scene.nightPreset === id ? '#ffd24a' : '#222',
              color: scene.nightPreset === id ? '#111' : '#ccc',
              border: '1px solid #444',
              borderRadius: 5,
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            {PRESET_LABEL[id]}
          </button>
        ))}
      </div>

      <label style={{ display: 'block', marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Night strength</span>
          <span style={{ color: '#ffd24a' }}>{Math.round(scene.night * 100)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={scene.night}
          style={{ width: '100%' }}
          onChange={(e) => setScene({ night: parseFloat(e.target.value) })}
        />
      </label>

      {/*
        The test that decides whether the tones are right before any color
        judgement is worth making.
      */}
      <label style={{ display: 'block', marginBottom: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={scene.nightMono === true}
          onChange={(e) => setScene({ nightMono: e.target.checked })}
        />{' '}
        gray (does the luminance alone read as evening?)
      </label>

      {stats && (
        <div style={{ marginBottom: 8, color: '#9aa', borderTop: '1px solid #333', paddingTop: 6 }}>
          <div>
            P50 {stats.p50.toFixed(3)} &nbsp; P75 {stats.p75.toFixed(3)}
          </div>
          <div>
            P90 {stats.p90.toFixed(3)} &nbsp; P95 {stats.p95.toFixed(3)}
          </div>
          <div>
            hot {(stats.hot * 100).toFixed(1)}% &nbsp; mean {stats.mean.toFixed(3)}
          </div>
          <div style={{ color: '#ffd24a' }}>
            scene compensation {ng.getCompensation().toFixed(3)}
          </div>
          <div style={{ color: '#ffd24a' }}>
            already graded, backing off {(ng.getBackoff() * 100).toFixed(0)}%
          </div>
          <div style={{ color: '#ffd24a' }}>
            property P50 {stats.propP50.toFixed(3)}, dark-source lift{' '}
            {(ng.getDarkLift() * 100).toFixed(0)}%
          </div>
        </div>
      )}

      <div style={{ borderTop: '1px solid #333', paddingTop: 6 }}>{FIELDS.map(row)}</div>

      <button
        onClick={() => {
          ng.clearOverrides()
          force((n) => n + 1)
        }}
        style={{
          width: '100%',
          marginTop: 6,
          padding: 5,
          background: '#222',
          color: '#ccc',
          border: '1px solid #444',
          borderRadius: 5,
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        reset to preset
      </button>
    </div>
  )
}
